import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { DEV_REL_STATUS, PROJECT_EMIRATES } from '@/lib/constants';

export default function DeveloperSlideOver({ open, onClose, developer, onSaved }) {
    const { profile } = useAuth();
    const isEdit = !!developer?.id;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    useEffect(() => {
        if (!open) return;
        setForm(developer ? { ...developer } : { company_name: '', relationship_status: 'Active', emirate: 'Dubai' });
    }, [open, developer?.id]);
    if (!open) return null;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null;
            payload.commission_pct = payload.commission_pct ? Number(payload.commission_pct) : null;
            let id = developer?.id;
            if (isEdit) {
                const { error } = await supabase.from('developers').update(payload).eq('id', id);
                if (error) throw error;
            } else {
                payload.organization_id = profile.organization_id;
                payload.created_by = profile.id;
                const { data, error } = await supabase.from('developers').insert(payload).select().single();
                if (error) throw error;
                id = data.id;
            }
            if (logoFile && id) {
                const ext = logoFile.name.split('.').pop();
                const path = `${profile.organization_id}/${id}/logo-${Date.now()}.${ext}`;
                const { error: upErr } = await supabase.storage.from('org').upload(path, logoFile, { upsert: true });
                if (!upErr) {
                    const { data: pub } = supabase.storage.from('org').getPublicUrl(path);
                    await supabase.from('developers').update({ logo_url: pub.publicUrl }).eq('id', id);
                }
            }
            toast.success(isEdit ? 'Developer updated' : 'Developer added');
            onSaved?.(); onClose();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/40" />
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" data-testid="developer-slideover">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold">{isEdit ? 'Edit developer' : 'Add developer'}</h2>
                    <button type="button" onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Company name *"><input required value={form.company_name||''} onChange={set('company_name')} className="input" data-testid="dev-company" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Contact person"><input value={form.contact_person||''} onChange={set('contact_person')} className="input" /></Field>
                        <Field label="Phone"><input value={form.phone||''} onChange={set('phone')} className="input" /></Field>
                        <Field label="WhatsApp"><input value={form.whatsapp||''} onChange={set('whatsapp')} className="input" /></Field>
                        <Field label="Email"><input type="email" value={form.email||''} onChange={set('email')} className="input" /></Field>
                        <Field label="Website"><input value={form.website||''} onChange={set('website')} className="input" placeholder="https://..." /></Field>
                        <Field label="Emirate"><select value={form.emirate||''} onChange={set('emirate')} className="input"><option value="">—</option>{PROJECT_EMIRATES.map((e) => <option key={e} value={e}>{e}</option>)}</select></Field>
                        <Field label="Default commission %"><input type="number" step="0.1" value={form.commission_pct||''} onChange={set('commission_pct')} className="input" /></Field>
                        <Field label="Relationship status"><select value={form.relationship_status||'Active'} onChange={set('relationship_status')} className="input">{DEV_REL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                    </div>
                    <Field label="Logo"><input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="text-[13px]" /></Field>
                    <Field label="Notes"><textarea rows={3} value={form.notes||''} onChange={set('notes')} className="input" /></Field>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-gray-300 font-medium">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-aks-navy text-white font-semibold disabled:opacity-60" data-testid="dev-submit">{saving ? 'Saving…' : 'Save'}</button>
                </div>
                <style>{`.input{margin-top:.25rem;width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #d1d5db;border-radius:.375rem;outline:none;font-size:14px;background:#fff}.input:focus{border-color:#C9A84C;box-shadow:0 0 0 3px rgba(201,168,76,.25)}textarea.input{height:auto;padding:.5rem .75rem}`}</style>
            </form>
        </div>
    );
}
function Field({ label, children }) { return <label className="block"><span className="text-[12px] font-medium text-aks-text">{label}</span>{children}</label>; }
